import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Home, Building2, Users, Store, Target, CalendarDays, MessagesSquare, User,
  Handshake, Bookmark, CreditCard, Settings as SettingsIcon, LogOut, Menu, X,
} from "lucide-react";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import InstallAppButton from "./InstallAppButton";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { section: "Home", items: [{ label: "My Hub", to: "/app", icon: Home }] },
  { section: "Discover", items: [
    { label: "Businesses", to: "/app/businesses", icon: Building2 },
    { label: "Professionals", to: "/app/professionals", icon: Users },
    { label: "Marketplace", to: "/app", icon: Store, soon: true },
  ]},
  { section: "Connect", items: [
    { label: "Interests", to: "/app/interests", icon: Handshake },
    { label: "Opportunities", to: "/app", icon: Target, soon: true },
    { label: "Events", to: "/app/events", icon: CalendarDays },
    { label: "Community", to: "/app", icon: MessagesSquare, soon: true },
  ]},
  { section: "My Hub", items: [
    { label: "My Profile", to: "/app/profile", icon: User },
    { label: "My Events", to: "/app/my-events", icon: CalendarDays },
    { label: "Connections", to: "/app", icon: Users, soon: true },
    { label: "Saved", to: "/app", icon: Bookmark, soon: true },
  ]},
  { section: "Account", items: [
    { label: "Membership", to: "/membership", icon: CreditCard, external: true },
    { label: "Settings", to: "/app/settings", icon: SettingsIcon },
  ]},
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initial = (user?.name || user?.email || "?").trim()[0]?.toUpperCase();
  const tierLabel = { free: "Free", standard: "Standard", premium: "Premium" }[user?.membership_tier] || "Free";

  const onLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 pt-6"><Logo /></div>

      <nav className="mt-6 flex-1 space-y-6 overflow-y-auto px-4 pb-6" data-testid="app-sidebar">
        {NAV.map((sec) => (
          <div key={sec.section}>
            <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">{sec.section}</p>
            <ul className="space-y-0.5">
              {sec.items.map((it) => {
                const Icon = it.icon;
                if (it.soon) {
                  return (
                    <li key={it.label}>
                      <span data-testid={`sidebar-soon-${it.label.toLowerCase()}`}
                        className="flex cursor-not-allowed items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm font-medium text-[color:var(--ink-soft)] opacity-70">
                        <span className="flex items-center gap-2.5"><Icon className="h-4 w-4" />{it.label}</span>
                        <span className="rounded-full bg-[color:var(--soft)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide">Soon</span>
                      </span>
                    </li>
                  );
                }
                if (it.external) {
                  return (
                    <li key={it.label}>
                      <Link to={it.to} onClick={onNavigate} data-testid={`sidebar-link-${it.label.toLowerCase()}`}
                        className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-[color:var(--ink-soft)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--ink)]">
                        <Icon className="h-4 w-4" />{it.label}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={it.label}>
                    <NavLink to={it.to} end onClick={onNavigate} data-testid={`sidebar-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) => `flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                        isActive ? "bg-[color:var(--soft)] text-[color:var(--ink)]" : "text-[color:var(--ink-soft)] hover:bg-[color:var(--soft)] hover:text-[color:var(--ink)]"}`}>
                      <Icon className="h-4 w-4" />{it.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-[color:var(--hairline)] px-4 py-4">
        <InstallAppButton variant="sidebar" className="mb-3" />
        <div className="mb-3 flex items-center gap-3 rounded-xl bg-[color:var(--soft)] p-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 rounded-full" />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--invert-bg)] text-sm font-semibold text-white">{initial}</span>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[color:var(--ink)]" data-testid="sidebar-user-name">{user?.name || user?.email}</p>
            <p className="text-[11px] text-[color:var(--ink-soft)]" data-testid="sidebar-user-tier">{tierLabel} member</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <ThemeToggle />
          <button onClick={onLogout} data-testid="sidebar-logout"
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)]">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex h-14 items-center justify-between border-b border-[color:var(--hairline)] bg-[color:var(--surface-glass)] px-4 backdrop-blur-xl">
        <Logo />
        <button onClick={() => setOpen(true)} data-testid="app-mobile-open"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)]">
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <div className="lg:grid lg:grid-cols-[260px_1fr]">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block sticky top-0 h-screen border-r border-[color:var(--hairline)] bg-[color:var(--surface)]">
          <SidebarContent />
        </aside>

        {/* Sidebar mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setOpen(false)} data-testid="app-mobile-drawer">
            <div className="absolute inset-0 bg-black/40" />
            <aside className="absolute right-0 top-0 h-full w-[86%] max-w-sm border-l border-[color:var(--hairline)] bg-[color:var(--surface)]" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-end p-3">
                <button onClick={() => setOpen(false)} className="rounded-full p-2 text-[color:var(--ink-soft)] hover:bg-[color:var(--soft)]"><X className="h-5 w-5" /></button>
              </div>
              <SidebarContent onNavigate={() => setOpen(false)} />
            </aside>
          </div>
        )}

        <main className="min-h-screen">{children}</main>
      </div>
    </div>
  );
}
